import React, {useMemo} from "react";
import "./ResultTable.css";
import {useSelector} from "react-redux";
import {RootState} from "../../store/rootReducer";
import {
    CheckboxVisibility,
    IColumn,
    IDetailsHeaderProps,
    IRenderFunction,
    Selection,
    SelectionMode,
    ShimmeredDetailsList,
    IconButton,
    IDragDropEvents} from '@fluentui/react';
import {renderSearchColumns} from '../../logic/Columns/ProductColumns';
import {useGetColumnsQuery, useGetFavoritesQuery} from "../../store/Api";
import {SEARCHCOLUMNS} from "../../Constants";
import {NormalizeResult} from "../../logic/Product";
import {INormalizedProduct} from "../../models/NormalizedProduct";
import {writeDraggedProducts} from "../../logic/productDrag";
import Sticky from "react-stickynode";
import { cloneDeep } from "lodash";
import {useGetOrganizationFeatureSettingsQuery} from "../../store/endpoints/OrganizationsEndpoints";
import {IProductBundleRef} from "../../models/ProductBundles";
import {usePersistedColumnLayout} from '../../hooks/usePersistedColumnLayout';

interface IResultTableProps {
    runningRealtime?: boolean;
    /** Where the column header sticks (px from the top). Default: just under the app header. */
    stickyTop?: number;
    refreshingVendors?: string[];
    onDetailsSelection: (p: INormalizedProduct) => void;
    products: INormalizedProduct[];
    isLoading?: boolean;
    selectedBundle?: IProductBundleRef;
    // Opt-in multi-select. When absent, the table behaves exactly as before (SelectionMode.none).
    onSelectionChanged?: (products: INormalizedProduct[]) => void;
    // Bump to clear the current row selection (e.g. after a bulk action or a new search).
    selectionResetToken?: number;
}

const ResultTable: React.FC<IResultTableProps> = (props) => {
    const stickyTop = props.stickyTop ?? 48;
    const selectionEnabled = !!props.onSelectionChanged;

    // Keep the latest callback in a ref so the (created-once) Selection never captures a stale one.
    const onSelectionChangedRef = React.useRef(props.onSelectionChanged);
    onSelectionChangedRef.current = props.onSelectionChanged;

    const selectionRef = React.useRef<Selection>();
    if (!selectionRef.current) {
        const selection: Selection = new Selection({
            onSelectionChanged: () =>
                onSelectionChangedRef.current?.(selection.getSelection() as unknown as INormalizedProduct[]),
            getKey: (item) => (item as INormalizedProduct).id,
        });
        selectionRef.current = selection;
    }

    React.useEffect(() => {
        if (props.selectionResetToken !== undefined) {
            selectionRef.current?.setAllSelected(false);
        }
    }, [props.selectionResetToken]);

    const org = useSelector((state: RootState) => state.settings.selectedOrganization);
    const favorites = useGetFavoritesQuery();
    const getColumns = useGetColumnsQuery();
    const sortBestPrice = useSelector((state: RootState) => state.search.sortBestPrice);
    const desiredQuantity = useSelector((state: RootState) => state.search.desiredQuantity);
    const [normalized, setNormalized] = React.useState<INormalizedProduct[]>([]);
    const [sorted, setSorted] = React.useState<INormalizedProduct[]>([]);
    const getOrgFeatures = useGetOrganizationFeatureSettingsQuery(org ?? '', {skip: !org || org === ''});
    const bestPriceExcludeGlobal = useSelector(() => getOrgFeatures.data?.excludeFromBestPrice);
    const bestPriceExcludeSearch = useSelector((state: RootState) => state.search.bestPriceExclusion);

    React.useEffect(() => {
        const bpExclude = [...(bestPriceExcludeGlobal ?? []), ...(bestPriceExcludeSearch ?? [])]
        const norm = props.products.map(p => NormalizeResult(p, favorites.data ? favorites.data[p.id] : undefined, desiredQuantity, bpExclude));
        setNormalized(norm ?? []);
    }, [props.products, bestPriceExcludeSearch, bestPriceExcludeGlobal, favorites.data, desiredQuantity]);

    React.useEffect(() => {
        if (sortBestPrice === undefined) {
            setSorted(normalized);
            return;
        }
        setSorted(cloneDeep(normalized).sort((a, b) => {
            // Define categories for quantity comparison
            const getQuantityCategory = (product: INormalizedProduct) => {
                if (desiredQuantity === undefined || desiredQuantity === 1) return 0; // no desired quantity (default to sufficient)
                if (product.bestVendorOnHand === undefined || product.bestVendorOnHand === 0) return 2; // out of stock
                if (product.bestVendorOnHand >= desiredQuantity) return 0; // sufficient
                return 1; // insufficient
            };
    
            const aCategory = getQuantityCategory(a);
            const bCategory = getQuantityCategory(b);
    
            // If products are in different categories, sort by category
            if (aCategory !== bCategory) {
                return aCategory - bCategory;
            }
    
            // If products are in the same category, sort by price
            if (a.bestPrice === undefined && b.bestPrice === undefined) {
                return 0;
            }
            if (a.bestPrice === undefined) {
                return 1;
            }
            if (b.bestPrice === undefined) {
                return -1;
            }
            return sortBestPrice === 'ASC'
                ? a.bestPrice - b.bestPrice
                : b.bestPrice - a.bestPrice;
        }));
    }, [normalized, sortBestPrice, desiredQuantity]);


    const columns = getColumns.data
        ? renderSearchColumns(getColumns.data.searchColumns, props.runningRealtime ?? false, false, props.refreshingVendors, props.selectedBundle)
        : renderSearchColumns(SEARCHCOLUMNS, props.runningRealtime ?? false, false, props.refreshingVendors, props.selectedBundle);
        
    const detailsButton: IColumn = {
        key: 'details',
        name: '',
        fieldName: 'details',
        minWidth: 40,
        maxWidth: 40,
        onRender:(product: INormalizedProduct) => <IconButton
            iconProps={{iconName: 'Edit'}}
            title='Details'
            ariaLabel={`Details for ${product.name ?? 'product'}`}
            onClick={() => {
                props.onDetailsSelection(product)
            }}
        />
    }
    columns.push(detailsButton);

    // Dragged widths and drop-reordered columns are remembered in this browser (per grid), the same
    // way the workspace grid does it.
    const {listRef, arrange, onColumnResize, onColumnIsSizingChanged, columnReorderOptions, applySavedWidths} = usePersistedColumnLayout('searchResults');
    const arrangedColumns = arrange(columns);
    React.useEffect(() => { applySavedWidths(arrangedColumns); });

    const onRenderDetailsHeader: IRenderFunction<IDetailsHeaderProps> = (props, defaultRender) => {
        if (!props) {
            return null;
        }
        // onColumnIsSizingChanged is a header prop: it lets the layout hook persist only real sizer drags.
        return (<Sticky enabled={true} top={stickyTop} innerZ={10}>{defaultRender?.({...props, onColumnIsSizingChanged})}</Sticky>);
    };

    // Rows drag out as products (native drag, not the list's own reorder): the docked bundle editor
    // drops them in. Dragging a selected row carries the whole selection.
    const dragDropEvents = useMemo<IDragDropEvents>(() => ({
        canDrag: () => true,
        canDrop: () => false,
        onDragStart: (item?: INormalizedProduct, _index?: number, selectedItems?: INormalizedProduct[], event?: MouseEvent) => {
            const dataTransfer = (event as unknown as DragEvent | undefined)?.dataTransfer;
            if (!dataTransfer || !item) return;
            const dragged = selectedItems && selectedItems.some((s) => s?.id === item.id) ? selectedItems : [item];
            writeDraggedProducts(dataTransfer, dragged);
        },
    }), []);

    return (
        <ShimmeredDetailsList
                // Scopes the row-centering styles (thumbnail, checkbox, text, buttons on one vertical line).
                className='result-table'
                items={sorted}
                dragDropEvents={dragDropEvents}
                enableShimmer={props.isLoading || favorites.isFetching}
                columns={arrangedColumns}
                componentRef={listRef}
                onColumnResize={onColumnResize}
                columnReorderOptions={columnReorderOptions}
                // Without a stable setKey, DetailsList wipes the row selection every time the
                // items array identity changes (which happens on every parent re-render).
                setKey="searchResults"
                getKey={(item) => (item as INormalizedProduct)?.id}
                // DetailsList only reads `selection` in its constructor, so it must be passed from
                // the very first render — selectionEnabled can flip after mount (claims load async).
                selection={selectionRef.current}
                selectionMode={selectionEnabled ? SelectionMode.multiple : SelectionMode.none}
                checkboxVisibility={selectionEnabled ? CheckboxVisibility.always : undefined}
                selectionPreservedOnEmptyClick={selectionEnabled}
                onRenderDetailsHeader={onRenderDetailsHeader}
            />
    );
};

export default ResultTable;
